import { supabase } from '../lib/supabase';

export interface UserProduct {
  id: string;
  name: string;
  description: string;
  price: number;
  original_price?: number;
  category_id: string;
  subcategory_id: string;
  categories?: string[];
  images: string[];
  colors: string[];
  custom_colors: string[];
  size_system: string;
  sizes: string[];
  condition: 'new' | 'used' | 'refurbished';
  brand?: string;
  model?: string;
  sku?: string;
  weight?: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
  tags: string[];
  seller_id: string;
  is_active: boolean;
  is_featured: boolean;
  is_sold: boolean;
  number_sold: number;
  view_count: number;
  favorite_count: number;
  created_at: string;
  updated_at: string;
  // Job-specific fields (only used when is_job = true)
  is_job?: boolean;
  salary_type?: 'hourly' | 'fixed' | 'negotiable';
  location?: string;
  job_type?: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship' | 'temporary' | 'volunteer';
  experience_level?: 'entry-level' | 'mid-level' | 'senior-level' | 'executive';
  duration?: string;
  remote_work_allowed?: boolean;
  required_skills?: string[];
  job_requirements?: string[];
  benefits_perks?: string[];
  contact_email?: string;
  application_deadline?: string;
  expected_start_date?: string;
  // Auction fields
  auction_id?: string;
  auction?: {
    id: string;
    end_time: string;
    current_bid: number;
    status: string;
  };
  // Joined data
  category_name?: string;
  subcategory_name?: string;
  // Computed fields for display
  sold?: number;
  stock?: number;
  rating?: number;
  reviews?: number;
}

export interface ProductStats {
  totalProducts: number;
  activeProducts: number;
  soldProducts: number;
  totalViews: number;
  totalFavorites: number;
  averageRating: number;
}

export class UserProductService {
  // Get all products for the current user
  static async getUserProducts(): Promise<UserProduct[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to fetch products');
      }

      const { data: products, error } = await supabase
        .from('products')
        .select(`*`)
        .eq('seller_id', user.id)
        .eq('is_job', false)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user products:', error);
        throw new Error(`Failed to fetch products: ${error.message}`);
      }

      // Transform the data to include category and subcategory names
      const transformedProducts: UserProduct[] = products.map(product => ({
        ...product,
        category_name: Array.isArray(product.categories) && product.categories.length > 0 ? product.categories[0] : 'Unknown',
        subcategory_name: Array.isArray(product.categories) && product.categories.length > 1 ? product.categories[1] : '',
        // For now, we'll use placeholder values for computed fields
        // These would typically come from separate analytics tables
        sold: Math.floor(Math.random() * 50), // Placeholder - would come from order_items
        stock: Math.floor(Math.random() * 100) + 10, // Placeholder - would come from inventory
        rating: Math.round((4.0 + Math.random() * 1.0) * 10) / 10, // Placeholder - would come from reviews, rounded to 1 decimal
        reviews: Math.floor(Math.random() * 20), // Placeholder - would come from reviews
        // Preserve all job-specific fields (even though this method filters for is_job = false)
        is_job: product.is_job,
        salary_type: product.salary_type,
        location: product.location,
        job_type: product.job_type,
        experience_level: product.experience_level,
        duration: product.duration,
        remote_work_allowed: product.remote_work_allowed,
        required_skills: product.required_skills,
        job_requirements: product.job_requirements,
        benefits_perks: product.benefits_perks,
        contact_email: product.contact_email,
        application_deadline: product.application_deadline,
        expected_start_date: product.expected_start_date,
      }));

      return transformedProducts;
    } catch (error) {
      console.error('Error in getUserProducts:', error);
      throw error;
    }
  }

  // Get paginated products for the current user
  static async getUserProductsPaginated(page: number = 1, limit: number = 6): Promise<{
    products: UserProduct[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to fetch products');
      }

      // Calculate offset
      const offset = (page - 1) * limit;

      // First, get the total count
      const { count: totalCount, error: countError } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('seller_id', user.id)
        .eq('is_job', false);

      if (countError) {
        console.error('Error fetching products count:', countError);
        throw new Error(`Failed to fetch products count: ${countError.message}`);
      }

      // Then, get the paginated products with auction data
      const { data: products, error } = await supabase
        .from('products')
        .select(`
          *,
          auctions:auction_id(
            id,
            end_time,
            current_bid,
            status
          )
        `)
        .eq('seller_id', user.id)
        .eq('is_job', false)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching paginated user products:', error);
        throw new Error(`Failed to fetch products: ${error.message}`);
      }

      // Transform the data to include category and subcategory names
      const transformedProducts: UserProduct[] = (products || []).map(product => ({
        ...product,
        category_name: Array.isArray(product.categories) && product.categories.length > 0 ? product.categories[0] : 'Unknown',
        subcategory_name: Array.isArray(product.categories) && product.categories.length > 1 ? product.categories[1] : '',
        // Include auction data if available
        auction: product.auctions ? {
          id: product.auctions.id,
          end_time: product.auctions.end_time,
          current_bid: product.auctions.current_bid,
          status: product.auctions.status
        } : undefined,
        // For now, we'll use placeholder values for computed fields
        // These would typically come from separate analytics tables
        sold: Math.floor(Math.random() * 50), // Placeholder - would come from order_items
        stock: Math.floor(Math.random() * 100) + 10, // Placeholder - would come from inventory
        rating: Math.round((4.0 + Math.random() * 1.0) * 10) / 10, // Placeholder - would come from reviews, rounded to 1 decimal
        reviews: Math.floor(Math.random() * 20), // Placeholder - would come from reviews
        // Preserve all job-specific fields (even though this method filters for is_job = false)
        is_job: product.is_job,
        salary_type: product.salary_type,
        location: product.location,
        job_type: product.job_type,
        experience_level: product.experience_level,
        duration: product.duration,
        remote_work_allowed: product.remote_work_allowed,
        required_skills: product.required_skills,
        job_requirements: product.job_requirements,
        benefits_perks: product.benefits_perks,
        contact_email: product.contact_email,
        application_deadline: product.application_deadline,
        expected_start_date: product.expected_start_date,
      }));

      const totalPages = Math.ceil((totalCount || 0) / limit);

      return {
        products: transformedProducts,
        totalCount: totalCount || 0,
        totalPages,
        currentPage: page,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
      };
    } catch (error) {
      console.error('Error in getUserProductsPaginated:', error);
      throw error;
    }
  }

  // Get all jobs for the current user
  static async getUserJobs(): Promise<UserProduct[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to fetch jobs');
      }

      const { data: jobs, error } = await supabase
        .from('products')
        .select(`*`)
        .eq('seller_id', user.id)
        .eq('is_job', true)
        .order('created_at', { ascending: false });

      // Also check all products for this user to see what's in the database
      const { data: allProducts } = await supabase
        .from('products')
        .select('id, name, is_job, seller_id')
        .eq('seller_id', user.id);

      console.log('🔍 getUserJobs query result:', {
        userId: user.id,
        jobsCount: jobs?.length || 0,
        jobs: jobs?.map(j => ({ id: j.id, name: j.name, is_job: j.is_job })) || [],
        allProductsCount: allProducts?.length || 0,
        allProducts: allProducts?.map(p => ({ id: p.id, name: p.name, is_job: p.is_job })) || [],
        error: error?.message,
        timestamp: new Date().toISOString()
      });

      if (error) {
        console.error('Error fetching user jobs:', error);
        throw new Error(`Failed to fetch jobs: ${error.message}`);
      }

      // Transform the data to include category and subcategory names
      const transformedJobs: UserProduct[] = jobs.map(job => ({
        ...job,
        category_name: Array.isArray(job.categories) && job.categories.length > 0 ? job.categories[0] : 'Unknown',
        subcategory_name: Array.isArray(job.categories) && job.categories.length > 1 ? job.categories[1] : '',
        // For jobs, we'll use different placeholder values
        sold: Math.floor(Math.random() * 20), // Placeholder - would come from applications
        stock: 1, // Jobs typically have 1 position available
        rating: Math.round((4.0 + Math.random() * 1.0) * 10) / 10, // Placeholder - would come from reviews, rounded to 1 decimal
        reviews: Math.floor(Math.random() * 10), // Placeholder - would come from reviews
        // Preserve all job-specific fields
        is_job: job.is_job,
        salary_type: job.salary_type,
        location: job.location,
        job_type: job.job_type,
        experience_level: job.experience_level,
        duration: job.duration,
        remote_work_allowed: job.remote_work_allowed,
        required_skills: job.required_skills,
        job_requirements: job.job_requirements,
        benefits_perks: job.benefits_perks,
        contact_email: job.contact_email,
        application_deadline: job.application_deadline,
        expected_start_date: job.expected_start_date,
      }));

      return transformedJobs;
    } catch (error) {
      console.error('Error in getUserJobs:', error);
      throw error;
    }
  }

  // Get all jobs for a specific company (Company Dashboard)
  static async getCompanyJobs(companyId: string): Promise<UserProduct[]> {
    try {
      if (!companyId) {
        throw new Error('companyId is required to fetch company jobs');
      }

      const { data: jobs, error } = await supabase
        .from('products')
        .select(`*`)
        .eq('company_id', companyId)
        .eq('is_job', true)
        .order('created_at', { ascending: false });

      console.log('🔍 getCompanyJobs query result:', {
        companyId,
        jobsCount: jobs?.length || 0,
        jobs: jobs?.map((j: any) => ({ id: j.id, name: j.name, is_job: j.is_job, company_id: j.company_id })) || [],
        error: error?.message,
        timestamp: new Date().toISOString(),
      });

      if (error) {
        console.error('Error fetching company jobs:', error);
        throw new Error(`Failed to fetch company jobs: ${error.message}`);
      }

      const transformedJobs: UserProduct[] = (jobs || []).map((job: any) => ({
        ...job,
        category_name: Array.isArray(job.categories) && job.categories.length > 0 ? job.categories[0] : 'Unknown',
        subcategory_name: Array.isArray(job.categories) && job.categories.length > 1 ? job.categories[1] : '',
        sold: Math.floor(Math.random() * 20),
        stock: 1,
        rating: Math.round((4.0 + Math.random() * 1.0) * 10) / 10,
        reviews: Math.floor(Math.random() * 10),
        is_job: job.is_job,
        salary_type: job.salary_type,
        location: job.location,
        job_type: job.job_type,
        experience_level: job.experience_level,
        duration: job.duration,
        remote_work_allowed: job.remote_work_allowed,
        required_skills: job.required_skills,
        job_requirements: job.job_requirements,
        benefits_perks: job.benefits_perks,
        contact_email: job.contact_email,
        application_deadline: job.application_deadline,
        expected_start_date: job.expected_start_date,
      }));

      return transformedJobs;
    } catch (error) {
      console.error('Error in getCompanyJobs:', error);
      throw error;
    }
  }

  // Get product statistics for the current user
  static async getUserProductStats(): Promise<ProductStats> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to fetch product stats');
      }

      const { data: products, error } = await supabase
        .from('products')
        .select('is_active, is_sold, view_count, favorite_count')
        .eq('seller_id', user.id)
        .eq('is_job', false);

      if (error) {
        console.error('Error fetching product stats:', error);
        throw new Error(`Failed to fetch product stats: ${error.message}`);
      }

      const stats: ProductStats = {
        totalProducts: products.length,
        activeProducts: products.filter(p => p.is_active).length,
        soldProducts: products.filter(p => p.is_sold).length,
        totalViews: products.reduce((sum, p) => sum + (p.view_count || 0), 0),
        totalFavorites: products.reduce((sum, p) => sum + (p.favorite_count || 0), 0),
        averageRating: 4.2, // Placeholder - would come from reviews table
      };

      return stats;
    } catch (error) {
      console.error('Error in getUserProductStats:', error);
      throw error;
    }
  }

  // Delete a product
  static async deleteProduct(productId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to delete products');
      }

      // First, delete associated product images
      const { error: imagesError } = await supabase
        .from('product_images')
        .delete()
        .eq('product_id', productId);

      if (imagesError) {
        console.error('Error deleting product images:', imagesError);
        // Continue with product deletion even if image deletion fails
      }

      // Delete the product
      const { error: productError } = await supabase
        .from('products')
        .delete()
        .eq('id', productId)
        .eq('seller_id', user.id); // Ensure user can only delete their own products

      if (productError) {
        console.error('Error deleting product:', productError);
        throw new Error(`Failed to delete product: ${productError.message}`);
      }
    } catch (error) {
      console.error('Error in deleteProduct:', error);
      throw error;
    }
  }

  // Toggle product active status
  static async toggleProductStatus(productId: string, isActive: boolean): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to update products');
      }

      const { error } = await supabase
        .from('products')
        .update({ is_active: isActive })
        .eq('id', productId)
        .eq('seller_id', user.id); // Ensure user can only update their own products

      if (error) {
        console.error('Error updating product status:', error);
        throw new Error(`Failed to update product status: ${error.message}`);
      }
    } catch (error) {
      console.error('Error in toggleProductStatus:', error);
      throw error;
    }
  }

  // Get a single product by ID (for editing)
  static async getProductById(productId: string): Promise<UserProduct | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to fetch product');
      }

      const { data: product, error } = await supabase
        .from('products')
        .select(`*`)
        .eq('id', productId)
        .eq('seller_id', user.id) // Ensure user can only access their own products
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Product not found
        }
        console.error('Error fetching product:', error);
        throw new Error(`Failed to fetch product: ${error.message}`);
      }

      return {
        ...product,
        category_name: Array.isArray(product.categories) && product.categories.length > 0 ? product.categories[0] : 'Unknown',
        subcategory_name: Array.isArray(product.categories) && product.categories.length > 1 ? product.categories[1] : '',
        sold: Math.floor(Math.random() * 50),
        stock: Math.floor(Math.random() * 100) + 10,
        rating: 4.0 + Math.random() * 1.0,
        reviews: Math.floor(Math.random() * 20),
        // Preserve all job-specific fields
        is_job: product.is_job,
        salary_type: product.salary_type,
        location: product.location,
        job_type: product.job_type,
        experience_level: product.experience_level,
        duration: product.duration,
        remote_work_allowed: product.remote_work_allowed,
        required_skills: product.required_skills,
        job_requirements: product.job_requirements,
        benefits_perks: product.benefits_perks,
        contact_email: product.contact_email,
        application_deadline: product.application_deadline,
        expected_start_date: product.expected_start_date,
      };
    } catch (error) {
      console.error('Error in getProductById:', error);
      throw error;
    }
  }
}
